import React, { useState } from 'react';
import cn from 'classnames';
import numberCss from './NumberField/NumberField.scss';
import { isSliderDraft, parseSliderValue } from './Slider/slider-value';

type Props = {
	value: number | undefined;
	min: number;
	max: number;
	decimals?: number;
	/** Arrow Up/Down change the value by `step`, or by `shiftStep` with Shift held, as in NumberField */
	step?: number;
	shiftStep?: number;
	onCommit: (value: number) => void;
	className?: string;
	'aria-label': string;
};

/**
 * A number typed as text in the Inspector field style. Only digits within the precision can be typed; the value is
 * clamped and saved on blur or Enter, and Escape restores the current value. Arrow keys step it and save at once.
 */
export default function ValueInput({
	value,
	min,
	max,
	decimals = 0,
	step = 1,
	shiftStep = 10,
	onCommit,
	className,
	...props
}: Props) {
	// Text being typed; null shows the current value
	const [draft, setDraft] = useState<string | null>(null);
	const commit = () => {
		if (draft === null) return;
		setDraft(null);
		const next = parseSliderValue(draft, { min, max, decimals });
		if (next !== undefined && next !== value) onCommit(next);
	};

	return (
		<input
			type="text"
			inputMode="decimal"
			autoComplete="off"
			aria-label={props['aria-label']}
			className={cn(numberCss.pfInput, className)}
			value={draft ?? String(value ?? '')}
			onChange={event => {
				if (isSliderDraft(event.target.value, decimals)) setDraft(event.target.value);
			}}
			onBlur={commit}
			onKeyDown={event => {
				if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
					event.preventDefault();
					const current = parseSliderValue(draft ?? String(value ?? min), { min, max, decimals }) ?? min;
					const delta = (event.shiftKey ? shiftStep : step) * (event.key === 'ArrowUp' ? 1 : -1);
					const next = parseSliderValue(String(current + delta), { min, max, decimals });
					setDraft(null);
					if (next !== undefined && next !== value) onCommit(next);
				}
				if (event.key === 'Enter') event.currentTarget.blur();
				if (event.key === 'Escape') {
					event.stopPropagation();
					setDraft(null);
				}
			}}
		/>
	);
}
